import {
  CreateOrderRequest,
  OrderItemMappingRequest,
  OrderReportResponse,
  OrderResponse,
  RevenueResponse,
  TopSaleCupResponse,
  UpdateOrderRequest,
} from "../../../infrastructure/model/OrderModel";
import { OrderService } from "../../../infrastructure/services/OrderService";
import { GROUP_BY_ENUM, ORDER_STATUS_ENUM } from "../../constants/AppConst";
import { PageResponse } from "../../constants/AppTypes";
import HttpUtils from "../../utils/HttpUtils";
import { BaseCommonApiUseCaseDeps } from "./CommonApiUseCase";

export const getAllOrderUseCase = async (
  deps: BaseCommonApiUseCaseDeps & {
    page: number;
    limit: number;
    status: ORDER_STATUS_ENUM[];
    startDate?: number;
    endDate?: number;
  }
): Promise<PageResponse<OrderResponse[]>> => {
  const { page, limit, status, startDate, endDate, setLoading, showError } = deps;
  try {
    setLoading?.(true);
    let dataMapper: PageResponse<OrderResponse[]> = {
      data: [],
      total: 0,
    };
    const res = await OrderService.getAllOrders(page, limit, status, startDate, endDate);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        dataMapper = {
          data: res.data.data,
          total: res.data.total,
        };
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const getItemInOrderUseCase = async (
  deps: BaseCommonApiUseCaseDeps & { orderId: number }
): Promise<OrderItemMappingRequest[]> => {
  const { orderId, setLoading, showError } = deps;
  try {
    setLoading?.(true);
    let dataMapper: OrderItemMappingRequest[] = [];
    const res = await OrderService.getItemsInOrder(orderId);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        dataMapper = res.data;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const createOrderUseCase = async (
  deps: BaseCommonApiUseCaseDeps & { request: CreateOrderRequest }
): Promise<OrderResponse | undefined> => {
  const { request, setLoading, showError, showSuccess } = deps;
  try {
    setLoading?.(true);
    let dataMapper: OrderResponse | undefined = undefined;
    const res = await OrderService.createNewOrder(request);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        showSuccess?.("Đơn hàng đã được đặt thành công.");
        dataMapper = res.data;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const updateOrderUseCase = async (
  deps: BaseCommonApiUseCaseDeps & { orderId: number; request: CreateOrderRequest }
): Promise<OrderResponse | undefined> => {
  const { orderId, request, setLoading, showError, showSuccess } = deps;
  try {
    setLoading?.(true);
    let dataMapper: OrderResponse | undefined = undefined;
    const res = await OrderService.updateOrder(orderId, request);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        showSuccess?.("Đơn hàng đã được cập nhật thành công.");
        dataMapper = res.data;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const getTopHotSaleCupUseCase = async (
  deps: BaseCommonApiUseCaseDeps & { startDate?: number; endDate?: number }
): Promise<TopSaleCupResponse[]> => {
  const { startDate, endDate, setLoading, showError } = deps;
  try {
    setLoading?.(true);
    let dataMapper: TopSaleCupResponse[] = [];
    const res = await OrderService.getTopHotSaleCup(startDate, endDate);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        dataMapper = res.data;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const getRevenueUseCase = async (
  deps: BaseCommonApiUseCaseDeps & {
    page: number;
    limit: number;
    status: ORDER_STATUS_ENUM[];
    startDate?: number;
    endDate?: number;
  }
): Promise<RevenueResponse | undefined> => {
  const { page, limit, status, startDate, endDate, setLoading, showError } = deps;
  try {
    setLoading?.(true);
    let dataMapper: RevenueResponse | undefined = undefined;
    const res = await OrderService.getRevenue(page, limit, status, startDate, endDate);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        dataMapper = res.data;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const getReportUseCase = async (
  deps: BaseCommonApiUseCaseDeps & {
    startDate?: number;
    endDate?: number;
    groupBy?: GROUP_BY_ENUM;
  }
): Promise<OrderReportResponse[]> => {
  const { groupBy, startDate, endDate, setLoading, showError } = deps;
  try {
    setLoading?.(true);
    let dataMapper: OrderReportResponse[] = [];
    const res = await OrderService.getOrderReport(startDate, endDate, groupBy);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccessWithData(res) {
        dataMapper = res.data;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const updateOrderStatusAndUserInfoUseCase = async (
  deps: BaseCommonApiUseCaseDeps & {
    orderId: number;
    newStatus: ORDER_STATUS_ENUM;
    name?: string;
    phone?: string;
    address?: string;
  }
): Promise<boolean> => {
  const { orderId, newStatus, name, phone, address, setLoading, showError, showSuccess } = deps;
  try {
    setLoading?.(true);
    let dataMapper = false;
    const res = await OrderService.updateOrderStatusAndUserInfo(
      orderId,
      newStatus,
      name,
      phone,
      address
    );
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res?.message);
      },
      onSuccess() {
        dataMapper = true;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const updateOrderCounterDiscountUseCase = async (
  deps: BaseCommonApiUseCaseDeps & {
    request: UpdateOrderRequest;
  }
): Promise<boolean> => {
  const { request, setLoading, showError, showSuccess } = deps;
  try {
    setLoading?.(true);
    let dataMapper = false;
    const res = await OrderService.updateOrderCounterDiscount(request);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res?.message);
      },
      onSuccess() {
        dataMapper = true;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const deleteOrderUseCase = async (
  deps: BaseCommonApiUseCaseDeps & { orderId: number; password: string }
): Promise<boolean> => {
  const { orderId, password, setLoading, showError, showSuccess } = deps;
  try {
    setLoading?.(true);
    let dataMapper = false;
    const res = await OrderService.deleteOrder(orderId, password);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccess() {
        showSuccess?.("Xoá đơn hàng thành công");
        dataMapper = true;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};

export const returnOrderUseCase = async (
  deps: BaseCommonApiUseCaseDeps & { orderId: number; password: string }
): Promise<boolean> => {
  const { orderId, password, setLoading, showError, showSuccess } = deps;
  try {
    setLoading?.(true);
    let dataMapper = false;
    const res = await OrderService.returnOrder(orderId, password);
    HttpUtils.handleResponse(res, {
      onDefaultError() {
        showError?.(res.message);
      },
      onSuccess() {
        showSuccess?.("Hoàn đơn hàng thành công");
        dataMapper = true;
      },
    });
    return dataMapper;
  } finally {
    setLoading?.(false);
  }
};
